/**
 * PlayerListSubmenu View Component
 *
 * Displays a list of recent players or an empty state message.
 * @module components/HeaderCreateMenu/PlayerListSubmenu
 */

import { memo } from "react";

import { Box } from "@/components/atoms/Box";
import { Text } from "@/components/atoms/Text";
import { Stack } from "@/components/atoms/Stack";

import PlayerSubmenuItem from "../PlayerSubmenuItem";

/**
 * Recent player item for display in menus
 */
interface RecentPlayerItem {
  id: string;
  name: string;
}

/**
 * Props for PlayerListSubmenuView component
 */
interface PlayerListSubmenuViewProps {
  players: readonly RecentPlayerItem[];
  onSelectPlayer: (playerId: string) => void;
  playerListClassName: string;
  sectionLabelClassName: string;
  emptyTextClassName: string;
  playerItemClassName: string;
  playerIconBoxClassName: string;
  playerIconClassName: string;
  playerNameClassName: string;
}

/**
 * Displays a list of recent players with the ability to select one.
 * Shows an empty state message if no players are available.
 * @param root0 - Component props
 * @param root0.players - Array of recent player items to display
 * @param root0.onSelectPlayer - Callback when a player is selected
 * @param root0.playerListClassName - Class name for the player list container
 * @param root0.sectionLabelClassName - Class name for the section label above player items
 * @param root0.emptyTextClassName - Class name for empty state text
 * @param root0.playerItemClassName - Class name for each player item
 * @param root0.playerIconBoxClassName - Class name for player icon container
 * @param root0.playerIconClassName - Class name for player icon
 * @param root0.playerNameClassName - Class name for player name text
 */
const PlayerListSubmenuView = ({
  players,
  onSelectPlayer,
  playerListClassName,
  sectionLabelClassName,
  emptyTextClassName,
  playerItemClassName,
  playerIconBoxClassName,
  playerIconClassName,
  playerNameClassName,
}: PlayerListSubmenuViewProps) =>
  players.length === 0 ? (
    <Box className={playerListClassName}>
      <Text variant="unstyled" className={emptyTextClassName}>
        View a player to see them here
      </Text>
    </Box>
  ) : (
    <Stack className={playerListClassName}>
      <Text variant="unstyled" className={sectionLabelClassName}>
        Recent Players
      </Text>
      {players.map(player => (
        <PlayerSubmenuItem
          key={player.id}
          player={player}
          onSelectPlayer={onSelectPlayer}
          itemClassName={playerItemClassName}
          iconBoxClassName={playerIconBoxClassName}
          iconClassName={playerIconClassName}
          nameClassName={playerNameClassName}
        />
      ))}
    </Stack>
  );

PlayerListSubmenuView.displayName = "PlayerListSubmenuView";

export default memo(PlayerListSubmenuView);
